import { useNavigate } from 'react-router-dom';
import { BookOpen, Calendar, Pause, Play, Square, Eye } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Task } from '../hooks/useTasks';
import type { Instance } from '../types/instance';
import { InstanceStatus } from '../types/instance';
import { usePauseInstance, useResumeInstance, useStopInstance } from '../hooks/useInstances';

const STATUS_DOT_COLOR: Record<string, string> = {
  [InstanceStatus.RUNNING]: 'bg-green-500',
  [InstanceStatus.PAUSED]: 'bg-yellow-500',
  [InstanceStatus.STARTING]: 'bg-blue-500',
  [InstanceStatus.PENDING]: 'bg-blue-500',
};

interface TaskCardProps {
  task: Task;
  agentName?: string;
  instance?: Instance;
}

export default function TaskCard({ task, agentName, instance }: TaskCardProps) {
  const navigate = useNavigate();
  const pauseInstance = usePauseInstance();
  const resumeInstance = useResumeInstance();
  const stopInstance = useStopInstance();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { task },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleInstanceAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const isRunning = instance?.status === InstanceStatus.RUNNING;
  const isPaused = instance?.status === InstanceStatus.PAUSED;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => navigate(`/tasks/${task.id}`)}
      className="cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
    >
      <p className="text-sm font-medium text-slate-900">{task.title}</p>
      <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
        <span>{agentName ?? 'Unassigned'}</span>
        {task.knowledge != null && (
          <span className="flex items-center gap-0.5" title="Has knowledge">
            <BookOpen className="h-3 w-3" />
          </span>
        )}
        <span className="flex items-center gap-0.5">
          <Calendar className="h-3 w-3" />
          {new Date(task.createdAt).toLocaleDateString()}
        </span>
      </div>

      {instance && (
        <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className={`h-2 w-2 rounded-full ${STATUS_DOT_COLOR[instance.status] ?? 'bg-slate-400'}`} />
            <span className="truncate max-w-[80px]">{instance.agent?.name ?? 'Agent'}</span>
            <span className="text-slate-300">|</span>
            <span>Turn {instance.currentTurn}/{instance.maxTurns}</span>
            <span className="text-slate-300">|</span>
            <span>${Number(instance.totalCostUsd).toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-0.5">
            {isRunning && (
              <button
                onClick={(e) => handleInstanceAction(e, () => pauseInstance.mutate(instance.id))}
                className="rounded p-0.5 text-slate-400 hover:bg-yellow-50 hover:text-yellow-600"
                title="Pause instance"
              >
                <Pause className="h-3 w-3" />
              </button>
            )}
            {isPaused && (
              <button
                onClick={(e) => handleInstanceAction(e, () => resumeInstance.mutate({ id: instance.id }))}
                className="rounded p-0.5 text-slate-400 hover:bg-green-50 hover:text-green-600"
                title="Resume instance"
              >
                <Play className="h-3 w-3" />
              </button>
            )}
            {(isRunning || isPaused) && (
              <button
                onClick={(e) => handleInstanceAction(e, () => stopInstance.mutate(instance.id))}
                className="rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                title="Stop instance"
              >
                <Square className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={(e) => handleInstanceAction(e, () => navigate(`/instances/${instance.id}`))}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="View instance"
            >
              <Eye className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
